import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Target,
  TrendingUp,
  MapPin,
  DollarSign,
  Users,
  Sparkles,
  Award,
  BookOpen,
  Briefcase,
  BarChart3,
  FileText,
  Building,
  MessageSquare,
  Zap,
  ArrowRight,
  CheckCircle,
  Lock,
  Rocket
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import LockedInsightCard from '../components/LockedInsightCard';
import RoadmapTeaserSection from '../components/RoadmapTeaserSection';
import RoadmapRefinementModal from '../components/RoadmapRefinementModal';
import LoadingSpinner from '../components/LoadingSpinner';
import authService from '../firebase/authService';
import { GridBackground } from '../components/TechUI';
import { paymentAPI, careerAPI } from '../utils/api';
import { usePricing } from '../utils/PricingContext';

const FreeResultsPage = () => {
  const navigate = useNavigate();
  const { getStarterPlan, formatPrice } = usePricing();
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedCareer, setSelectedCareer] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [hasFreeTierData, setHasFreeTierData] = useState(false);
  const [showRefinementModal, setShowRefinementModal] = useState(false);
  const [selectedCareerForRoadmap, setSelectedCareerForRoadmap] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Check authentication
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);

      // Check subscription status if user is logged in
      let premiumStatus = false;
      if (currentUser) {
        try {
          const subResponse = await paymentAPI.getUserSubscription();
          console.log('🔍 Full subscription response:', JSON.stringify(subResponse, null, 2));
          const subscription = subResponse?.subscription || subResponse;
          console.log('🔍 Subscription object:', subscription);
          console.log('🔍 Subscription tier:', subscription?.tier);
          console.log('🔍 Tier type:', typeof subscription?.tier);
          console.log('🔍 Is free?', subscription?.tier === 'free');

          premiumStatus = subscription &&
            subscription.tier &&
            subscription.tier !== 'free' &&
            subscription.tier !== null &&
            subscription.tier !== undefined;

          console.log('✅ Premium status calculated:', premiumStatus);
          setIsPremium(premiumStatus);
          console.log('✅ isPremium state set to:', premiumStatus);
        } catch (error) {
          console.error('❌ Error checking subscription:', error);
          console.error('❌ Error details:', error.response?.data || error.message);
          // Default to free if check fails
          setIsPremium(false);
        }
      } else {
        console.log('⚠️ No current user, defaulting to free');
        setIsPremium(false);
      }

      // Load career suggestions from localStorage
      const savedSuggestions = localStorage.getItem('careerSuggestions');
      console.log('=== FREE RESULTS PAGE DEBUG ===');
      console.log('Loaded from localStorage:', savedSuggestions);
      console.log('Is Premium:', premiumStatus);

      if (savedSuggestions) {
        try {
          const parsed = JSON.parse(savedSuggestions);
          console.log('Parsed suggestions:', parsed);
          console.log('Career paths count:', parsed?.career_paths?.length);
          console.log('Is premium data:', parsed?.is_premium);
          console.log('Has user_summary:', !!parsed?.user_summary);

          // If user is premium but data is free tier, show message
          if (premiumStatus && !parsed.is_premium) {
            console.log('⚠️ Premium user has free tier data - showing upgrade prompt');
            setHasFreeTierData(true);
            // Still show the data, but user can regenerate
          } else {
            setHasFreeTierData(false);
          }

          setSuggestions(parsed);
          setLoading(false);
        } catch (e) {
          console.error('Failed to parse suggestions:', e);
          toast.error('Failed to load career insights. Please try again.');
          setLoading(false);
          // Don't navigate immediately - give user time to see error
          setTimeout(() => navigate('/'), 3000);
        }
      } else {
        console.error('No career suggestions found in localStorage');
        toast('Let\'s build your profile first to get career insights!', { icon: '👋' });
        navigate('/build-roadmap', { state: { returnTo: '/free-results' } });
      }
    };

    loadData();
  }, [navigate]);

  const handleUpgradeClick = () => {
    // If not logged in, show auth modal first
    if (!user) {
      toast('Please sign in to upgrade to premium', {
        icon: '🔐',
      });
      // In a real app, show auth modal here
      // For now, navigate to subscription page
      navigate('/subscribe');
    } else {
      navigate('/subscribe');
    }
  };

  const lockedInsights = [
    {
      icon: FileText,
      title: "Resume Improvement Report",
      teaser: "Get 10+ actionable insights to optimize your resume for ATS and recruiters"
    },
    {
      icon: BarChart3,
      title: "Full Skill Gap Analysis",
      teaser: "Detailed breakdown of 15+ skills with learning paths and priority recommendations"
    },
    {
      icon: TrendingUp,
      title: "Salary Growth Projection",
      teaser: "See your earning potential over 1, 3, and 5 years with market data and trends"
    },
    {
      icon: Building,
      title: "Company Recommendations",
      teaser: "Top 20+ companies hiring for your target role with application strategies"
    },
    {
      icon: Rocket,
      title: "Portfolio Project Builder",
      teaser: "3-5 industry-specific projects with complete implementation guides"
    },
    {
      icon: MessageSquare,
      title: "Interview Preparation",
      teaser: "Role-specific questions with AI-powered answer evaluation and feedback"
    },
    {
      icon: Users,
      title: "Mentor Matching",
      teaser: "Connect with experienced professionals in your target field"
    },
    {
      icon: Award,
      title: "Certification Roadmap",
      teaser: "Industry certifications that boost your credibility and earning potential"
    }
  ];

  if (loading || isGenerating) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex flex-col items-center justify-center">
        <LoadingSpinner />
        {isGenerating && (
          <div className="mt-8 text-center animate-pulse">
            <p className="text-emerald-400 font-mono text-lg">Synthesizing your career blueprint...</p>
            <p className="text-slate-400 font-mono text-sm mt-2">This may take up to 30 seconds</p>
          </div>
        )}
      </div>
    );
  }

  console.log('=== RENDER CHECK ===');
  console.log('Loading:', loading);
  console.log('Suggestions:', suggestions);
  console.log('Suggestions type:', typeof suggestions);
  console.log('Has career_paths property:', 'career_paths' in (suggestions || {}));
  console.log('Career paths value:', suggestions?.career_paths);
  console.log('Career paths length:', suggestions?.career_paths?.length);

  if (!suggestions || !suggestions.career_paths || suggestions.career_paths.length === 0) {
    console.error('Rendering "no insights" message because:', {
      noSuggestions: !suggestions,
      noCareerPaths: !suggestions?.career_paths,
      emptyArray: suggestions?.career_paths?.length === 0
    });

    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl font-mono mb-4">No career insights available</p>
          <button
            onClick={() => navigate('/build-roadmap', { state: { returnTo: '/free-results' } })}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition-all font-mono"
          >
            Build My Profile
          </button>
        </div>
      </div>
    );
  }

  const careerPaths = suggestions.career_paths || [];
  const storedProfile = (() => {
    try {
      const raw = localStorage.getItem('userProfile');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();
  console.log('About to render with career paths:', careerPaths.length);

  const handleRefinementSubmit = async (refinementAnswers) => {
    setShowRefinementModal(false);

    try {
      let userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      if (!userProfile || Object.keys(userProfile).length === 0) {
        toast('We need a bit more info to customize your roadmap!', { icon: 'ℹ️' });
        navigate('/build-roadmap', { state: { returnTo: '/free-results' } });
        return;
      }

      setIsGenerating(true);
      toast.loading('Generating personalized roadmap...', { id: 'roadmapGen' });
      console.log('🚀 Generating roadmap for:', selectedCareerForRoadmap.title);
      console.log('🚀 Refinement answers:', refinementAnswers);
      console.log('🚀 User profile:', userProfile);

      // Generate roadmap with refinement answers
      const roadmapData = await careerAPI.generateRoadmap(
        userProfile,
        selectedCareerForRoadmap.title,
        null, // No orderId needed for premium
        refinementAnswers
      );

      console.log('✅ Roadmap generated successfully!');
      console.log('✅ Full roadmap data:', JSON.stringify(roadmapData, null, 2));
      console.log('✅ Roadmap weeks:', roadmapData?.weeks);
      console.log('✅ Roadmap weeks length:', roadmapData?.weeks?.length);

      // Validate roadmap data before saving
      if (!roadmapData || !roadmapData.weeks || roadmapData.weeks.length === 0) {
        console.error('❌ Invalid roadmap data structure');
        toast.dismiss('roadmapGen');
        setIsGenerating(false);
        toast.error('Generated roadmap is incomplete. Please try again.');
        return;
      }

      // Save to localStorage
      localStorage.setItem('roadmapData', JSON.stringify(roadmapData));
      localStorage.setItem('selectedCareer', JSON.stringify(selectedCareerForRoadmap));
      if (roadmapData.roadmap_id) {
        localStorage.setItem('selectedRoadmapId', roadmapData.roadmap_id);
      } else {
        localStorage.removeItem('selectedRoadmapId');
      }

      toast.dismiss('roadmapGen');
      toast.success(`Personalized roadmap with ${roadmapData.weeks.length} weeks generated!`);
      // No need to set isGenerating to false since we navigate away immediately
      navigate('/roadmap');
    } catch (error) {
      setIsGenerating(false);
      console.error('❌ Error generating roadmap:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.dismiss('roadmapGen');

      // If it's a limit error, show upgrade prompt instead of toast
      if (error.isLimitError && error.limitInfo) {
        // Store limit error to show in UI
        setShowRefinementModal(false);
        // We'll show the upgrade prompt in the UI
        // For now, show a toast with upgrade link
        toast.error(
          <div>
            <p>{error.limitInfo.message}</p>
            <button
              onClick={() => navigate('/subscribe')}
              className="mt-2 text-emerald-400 underline"
            >
              Upgrade Now
            </button>
          </div>,
          { duration: 5000 }
        );
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate roadmap';
        toast.error(errorMsg);
      }
    }
  };

  return (
    <GridBackground className="min-h-screen">
      {/* Refinement Modal */}
      <RoadmapRefinementModal
        isOpen={showRefinementModal}
        onClose={() => setShowRefinementModal(false)}
        onSubmit={handleRefinementSubmit}
        careerPath={selectedCareerForRoadmap?.title || ''}
      />
      <div className="relative z-20 max-w-7xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-6 py-3 mb-6">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-mono uppercase text-sm font-bold">Analysis Complete</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-mono">
            {storedProfile.name ? `${storedProfile.name.split(' ')[0].toUpperCase()}'S` : 'YOUR'} CAREER <span className="text-emerald-400">BLUEPRINT</span>
          </h1>

          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8 font-mono">
            Based on your skills, experience and goals, we've identified the top 3 paths that best fit your profile and lifestyle.
          </p>

          {suggestions.user_summary && (
            <div className="max-w-4xl mx-auto bg-[#0F131F]/80 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-start space-x-4">
                <Brain className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                <p className="text-slate-300 text-left font-mono">{suggestions.user_summary}</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Your Profile Snapshot */}
        {storedProfile && Object.keys(storedProfile).length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="md:col-span-2 bg-[#0F131F]/80 border border-white/10 rounded-2xl p-6 backdrop-blur-sm text-left">
              <h3 className="text-sm font-mono text-emerald-400 uppercase mb-3">Your Profile at a Glance</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm font-mono text-slate-200">
                <div>
                  <p className="text-slate-500 text-xs uppercase mb-1">Current Role & Experience</p>
                  <p>{storedProfile.currentJob || 'Not set'} • {storedProfile.experience || '0-1 years'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase mb-1">Industries & Work Style</p>
                  <p>{storedProfile.industries?.length ? storedProfile.industries.join(', ') : 'No industries selected yet'}</p>
                  <p className="text-slate-400 text-xs mt-1">Prefers {storedProfile.workPreference || 'any'} work setup</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase mb-1">Primary Goal</p>
                  <p>{storedProfile.careerGoals || 'Clarify next steps'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase mb-1">Weekly Commitment</p>
                  <p>{storedProfile.timeAvailable || 'Not specified'} / week</p>
                </div>
              </div>
            </div>
            <div className="bg-[#050814]/80 border border-emerald-500/40 rounded-2xl p-5 text-left">
              <h4 className="text-sm font-mono text-emerald-400 uppercase mb-2">How to read these results</h4>
              <ul className="space-y-2 text-xs font-mono text-slate-300">
                <li>• Match Score shows alignment with your background & goals.</li>
                <li>• “Why this fits you” is generated from your inputs, not generic text.</li>
                <li>• Skill gaps + time commitment will shape your personalised roadmap.</li>
              </ul>
            </div>
          </div>
        )}

        {/* Premium User with Free Tier Data Banner */}
        {isPremium && hasFreeTierData && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/50 rounded-xl p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white font-mono mb-2 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-emerald-400" />
                  Premium Access Detected
                </h3>
                <p className="text-slate-300 font-mono text-sm mb-4">
                  You have premium access, but your current suggestions were generated with free tier data.
                  Regenerate to see all career paths and complete skill gaps.
                </p>
                <button
                  onClick={async () => {
                    try {
                      let userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
                      if (!userProfile || Object.keys(userProfile).length === 0) {
                        toast('We need a bit more info to customize your roadmap!', { icon: 'ℹ️' });
                        navigate('/build-roadmap', { state: { returnTo: '/free-results' } });
                        return;
                      }

                      toast.loading('Regenerating suggestions with premium data...');
                      const newSuggestions = await careerAPI.suggestCareers({ profile: userProfile });
                      localStorage.setItem('careerSuggestions', JSON.stringify(newSuggestions));
                      setSuggestions(newSuggestions);
                      setHasFreeTierData(false);
                      toast.success('Suggestions regenerated with full premium data!');
                    } catch (error) {
                      console.error('Error regenerating suggestions:', error);
                      toast.error(error.message || 'Failed to regenerate suggestions');
                    }
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg font-mono font-semibold transition-all"
                >
                  Regenerate Suggestions
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Section A: 3 Career Paths */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-mono">YOUR TOP 3 CAREER PATHS</h2>
            <p className="text-slate-400 font-mono">Each path is scored based on your profile match and market demand</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {careerPaths.map((path, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-[#0F131F] border-2 rounded-2xl p-6 hover:border-emerald-500/50 transition-all cursor-pointer ${selectedCareer === index ? 'border-emerald-500' : 'border-white/10'
                  }`}
                onClick={() => setSelectedCareer(index)}
              >
                {/* Match Score */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-mono uppercase text-slate-500">Match Score</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                      <span className="text-2xl font-bold text-emerald-400 font-mono">{path.match_percentage}%</span>
                    </div>
                  </div>
                </div>

                {/* Title & Description */}
                <h3 className="text-2xl font-bold text-white mb-3 font-mono">{path.title}</h3>
                <p className="text-slate-400 mb-4 font-mono text-sm">{path.description}</p>

                {/* Why This Fits */}
                <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
                  <p className="text-xs font-mono uppercase text-emerald-400 mb-2">Why This Fits You</p>
                  <p className="text-sm text-slate-300 font-mono">{path.reasoning}</p>
                </div>

                {/* Job Market */}
                {path.job_market && (
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 font-mono flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        Avg. Salary
                      </span>
                      <span className="text-white font-mono font-bold">
                        ₹{(path.job_market.average_salary_inr || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 font-mono flex items-center">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Growth
                      </span>
                      <span className={`font-mono font-bold ${path.job_market.growth_outlook === 'high' ? 'text-emerald-400' :
                        path.job_market.growth_outlook === 'medium' ? 'text-yellow-400' :
                          'text-slate-400'
                        }`}>
                        {(path.job_market.growth_outlook || 'medium').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 font-mono flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        Remote
                      </span>
                      <span className="text-white font-mono">
                        {path.job_market.remote_friendly ? 'Yes' : 'Hybrid/Onsite'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Skill Gaps (Show all if premium, limited to 2 if free) */}
                <div className="space-y-2">
                  <p className="text-xs font-mono uppercase text-slate-500 mb-2">Skills to Learn</p>
                  {path.skill_gaps && (isPremium ? path.skill_gaps : path.skill_gaps.slice(0, 2)).map((gap, gapIndex) => {
                    if (gapIndex === 0) {
                      console.log('🔍 Rendering skill gaps - isPremium:', isPremium, 'total gaps:', path.skill_gaps?.length, 'showing:', isPremium ? path.skill_gaps.length : 2);
                    }
                    return (
                      <div key={gapIndex} className="flex items-center space-x-2 text-sm">
                        <div className={`w-2 h-2 rounded-full ${gap.importance === 'High' ? 'bg-red-400' :
                          gap.importance === 'Medium' ? 'bg-yellow-400' :
                            'bg-blue-400'
                          }`} />
                        <span className="text-slate-300 font-mono">{gap.skill}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${gap.importance === 'High' ? 'bg-red-500/20 text-red-400' :
                          gap.importance === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                          {gap.importance}
                        </span>
                      </div>
                    );
                  })}
                  {!isPremium && path.locked_content && path.locked_content.additional_skill_gaps > 0 ? (
                    <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Lock className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs text-yellow-400 font-mono font-bold">
                          +{path.locked_content.additional_skill_gaps} More Skills Locked
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* CTA */}
                {isPremium ? (
                  <div className="space-y-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCareerForRoadmap(path);
                        setShowRefinementModal(true);
                      }}
                      className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-4 rounded-xl font-semibold hover:scale-105 transition-all flex items-center justify-center space-x-2 font-mono"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>GENERATE ROADMAP</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/premium-dashboard');
                      }}
                      className="w-full bg-slate-800/50 border border-slate-700 text-white py-2 px-4 rounded-xl font-semibold hover:bg-slate-800 transition-all flex items-center justify-center space-x-2 font-mono text-sm"
                    >
                      <span>VIEW DASHBOARD</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgradeClick();
                    }}
                    className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-4 rounded-xl font-semibold hover:scale-105 transition-all flex items-center justify-center space-x-2 font-mono"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>VIEW FULL PATH</span>
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* Section B: Roadmap Teaser */}
        <section className="mb-20">
          <RoadmapTeaserSection
            careerPath={careerPaths[selectedCareer]?.title}
            onUnlock={handleUpgradeClick}
          />
        </section>

        {/* Section B2: Email-Gated PDF Report */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0F131F] to-[#0B0F19] border border-white/10 rounded-2xl p-8 text-center"
          >
            <FileText className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2 font-mono">DOWNLOAD YOUR CAREER REPORT</h3>
            <p className="text-slate-400 font-mono text-sm mb-6 max-w-lg mx-auto">
              Get a beautifully formatted PDF version of your career analysis — including skill gaps, salary data, and roadmap overview.
            </p>

            {(() => {
              const emailSubmitted = localStorage.getItem('ct_report_email');
              if (emailSubmitted) {
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-mono">
                      <CheckCircle className="w-4 h-4" />
                      <span>Report sent to {emailSubmitted}</span>
                    </div>
                    <button
                      onClick={() => {
                        toast.success('PDF download started!');
                        // In production, this would trigger actual PDF generation
                      }}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-8 rounded-xl font-semibold font-mono inline-flex items-center gap-2 hover:scale-105 transition-all"
                    >
                      <FileText className="w-4 h-4" />
                      Download PDF Report
                    </button>
                  </div>
                );
              }

              return (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const email = formData.get('email');
                    if (email && email.includes('@')) {
                      localStorage.setItem('ct_report_email', email);
                      toast.success('Report is being generated! Check your email shortly.');
                      // Force re-render
                      e.target.reset();
                      window.dispatchEvent(new Event('storage'));
                      // Reload component
                      setSuggestions({ ...suggestions });
                    } else {
                      toast.error('Please enter a valid email address');
                    }
                  }}
                  className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                >
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    required
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 font-mono text-sm"
                  />
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-6 rounded-xl font-semibold font-mono inline-flex items-center gap-2 hover:scale-105 transition-all whitespace-nowrap"
                  >
                    <FileText className="w-4 h-4" />
                    Get Free PDF
                  </button>
                </form>
              );
            })()}

            <p className="text-[10px] text-slate-600 font-mono mt-3">No spam. We'll also send you weekly career tips.</p>
          </motion.div>
        </section>

        {/* Section C: Locked Insights Grid - Only show if not premium */}
        {!isPremium ? (
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-mono">PREMIUM FEATURES</h2>
              <p className="text-slate-400 font-mono">Unlock comprehensive career tools and resources</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {lockedInsights.map((insight, index) => (
                <LockedInsightCard
                  key={index}
                  icon={insight.icon}
                  title={insight.title}
                  teaser={insight.teaser}
                  onClick={handleUpgradeClick}
                />
              ))}
            </div>
          </section>
        ) : null}

        {/* Section D: Sticky Pricing CTA - Only show if not premium */}
        {!isPremium ? (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="fixed bottom-0 left-0 right-0 bg-[#0F131F]/95 backdrop-blur-xl border-t border-white/10 p-4 z-50"
          >
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-bold text-white font-mono">READY TO UNLOCK YOUR CAREER PATH?</h3>
                <p className="text-sm text-slate-400 font-mono">Get full access to roadmaps, tools, and expert guidance</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-slate-500 font-mono uppercase">Starting at</p>
                  <p className="text-2xl font-bold text-emerald-400 font-mono">
                    {getStarterPlan() ? formatPrice(getStarterPlan().price, getStarterPlan().currency) : '...'}
                    <span className="text-sm"> for {getStarterPlan()?.duration_months || 1} month</span>
                  </p>
                </div>
                <button
                  onClick={handleUpgradeClick}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 px-8 rounded-xl font-semibold hover:scale-105 transition-all flex items-center space-x-2 font-mono whitespace-nowrap"
                >
                  <Zap className="w-5 h-5" />
                  <span>UPGRADE TO PREMIUM</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        ) : null}

        {/* Spacer for fixed bottom bar */}
        <div className="h-32"></div>
      </div>
    </GridBackground>
  );
};

export default FreeResultsPage;

